import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import DOMPurify from 'dompurify';
import { Copy, Check } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogFooter, 
  DialogTitle, 
  DialogDescription 
} from "@/shared/components/ui/dialog";
import { useToast } from "@/shared/hooks/use-toast";
import { Integration, IntegrationBase, IntegrationStatus, AuthField, FormField, IntegrationPostInstall } from "../types/integration";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { useViewScope } from "@/shared/hooks/use-view-scope";
import { PasswordField } from "@/shared/components/ui/password-field";
import { IntegrationBaseService } from '../services/integrationsBaseService';
import { translateDeep, getIntegrationDescription } from '@/shared/utils/i18nHelper';
import { analyticsService } from '@/shared/services/analytics';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';
import {
  IntegrationMetadataFields,
  missingRequiredMetadata,
} from './IntegrationMetadataFields';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/tabs';
import type { MetadataFieldDefinition } from '../types/integration';
import {
  modelsToEntries,
  entriesToModels,
  validateEntries,
  type ModelEntry,
} from '@/features/Integrations/utils/modelEntries';
import { IntegrationPostInstallDialog } from './IntegrationPostInstallDialog';

interface IntegrationsFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTemplate: Integration | null;
  selectedIntegration?: IntegrationBase | null; // Para modo de edição
  mode?: 'create' | 'edit'; // Modo do formulário
  onSuccess: () => void;
  /**
   * Organização dona da integração, quando NÃO é a ativa da sessão (AB#85605) — os
   * provedores de IA da plataforma, administrados em escopo de plataforma.
   */
  organizationId?: string;
}

export const IntegrationsForm = ({
  isOpen,
  onOpenChange,
  selectedTemplate,
  selectedIntegration,
  mode = 'create',
  onSuccess,
  organizationId
}: IntegrationsFormProps) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0]; // 'pt' ou 'en'
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();
  const { scope: viewScope, organizationId: viewOrganizationId } = useViewScope();
  // Em "Visão da organização" não há um workspace/produto selecionado: o escopo
  // tem que ser fixo em global e o toggle deve sumir. O mesmo vale para os provedores da
  // PLATAFORMA (AB#85605): a organização da plataforma não tem workspaces, então oferecer
  // "vincular a um produto" seria oferecer uma escolha sem destino.
  const isOrgView = viewScope === 'organization' || !!organizationId;
  const effectiveOrgId = organizationId || currentWorkspace?.organization_id || viewOrganizationId || null;
  
  // Traduzir template e integration
  const translatedTemplate = useMemo(() => {
    if (!selectedTemplate) return null;
    return translateDeep(selectedTemplate, currentLang);
  }, [selectedTemplate, currentLang]);
  
  const translatedIntegration = useMemo(() => {
    if (!selectedIntegration) return null;
    return translateDeep(selectedIntegration, currentLang);
  }, [selectedIntegration, currentLang]);
  
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [isConnecting, setIsConnecting] = useState(false);
  const [isGlobal, setIsGlobal] = useState(false);

  // Sem produto possível (visão da organização ou plataforma): força global e trava.
  useEffect(() => {
    if (isOrgView && !isGlobal) setIsGlobal(true);
  }, [isOrgView, isGlobal]);
  const [integrationTitle, setIntegrationTitle] = useState('');
  /**
   * O catálogo de modelos da instalação (AB#85605). Só aparece quando o cadastro tem o que
   * editar: no Azure ele chega preenchido pela sincronização com o provedor; nos demais, é
   * o catálogo do template, onde se acrescenta um modelo novo sem esperar atualização.
   */
  const [modelEntries, setModelEntries] = useState<ModelEntry[]>([]);
  const [activeTab, setActiveTab] = useState<'settings' | 'metadata'>('settings');

  /**
   * O campo de metadados que o TEMPLATE declara (AB#85605).
   *
   * A aba só existe quando o cadastro diz que há o que editar — hoje, a lista de modelos do
   * Azure OpenAI, onde cada deployment precisa ser declarado por quem instala. Provedor de
   * catálogo fixo não declara nada e o diálogo segue como sempre foi.
   */
  const metadataFields = useMemo<MetadataFieldDefinition[]>(() => {
    const metadata = (mode === 'edit'
      ? translatedIntegration?.template?.metadata
      : translatedTemplate?.metadata) as Record<string, unknown> | undefined;
    const campos = Array.isArray(metadata?.form_fields) ? metadata!.form_fields : [];
    return (campos as MetadataFieldDefinition[]).filter((campo) => campo?.key && campo?.type);
  }, [mode, translatedIntegration, translatedTemplate]);

  const hasModelsField = metadataFields.some((campo) => campo.type === 'models');

  /** Valores dos campos de metadados que não são a lista de modelos. */
  const [metadataValues, setMetadataValues] = useState<Record<string, unknown>>({});
  const [postInstallInfo, setPostInstallInfo] = useState<IntegrationPostInstall | null>(null);
  const [postInstallLabel, setPostInstallLabel] = useState<string>('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Inicializar formData baseado no modo (create/edit)
  useEffect(() => {
    if (mode === 'edit' && translatedIntegration) {
      // Modo de edição: pré-popular com dados existentes
      const initialFormData: Record<string, unknown> = {
        label: translatedIntegration.label,
        description: getIntegrationDescription(translatedIntegration, i18n.language) || ''
      };
      
      // Inicializar campos de autenticação como VAZIOS por segurança (não carregar credenciais)
      if (translatedIntegration.template?.auth_fields?.fields) {
        translatedIntegration.template.auth_fields.fields.forEach((field: AuthField) => {
          // SEGURANÇA: Inicializar como vazio para forçar re-digitação
          initialFormData[field.key] = '';
        });
      }
      
      // Carregar valores dos campos de configuração (settings agora é um objeto simples)
      if (translatedIntegration.template?.settings?.form_fields) {
        translatedIntegration.template.settings.form_fields.forEach((field: FormField) => {
          const formKey = (field.key || field.name) as string;
          const storageKey = field.name || field.key;
          if (field.readonly) {
            // Campos readonly são computados mais abaixo (ex: webhook_url)
            return;
          }
          const value = storageKey
            ? (translatedIntegration.settings?.[storageKey] ?? translatedIntegration.settings?.[formKey])
            : undefined;
          if (value !== undefined) {
            initialFormData[formKey] = value;
          }
        });
      }

      // Injetar webhook_url computado para integrações communicator
      if (translatedIntegration.template?.settings?.form_fields?.some((f: FormField) => f.readonly && (f.key || f.name) === 'webhook_url')) {
        const adapterType = (translatedIntegration.settings as Record<string, unknown>)?.adapter_type as string | undefined;
        // Webhook é roteado pelo domínio do front (que faz proxy /api/communicator/* → backend),
        // então a base é a própria origem onde o app está servido, não a URL crua do backend.
        const baseUrl = window.location.origin;
        const webhookUrl = adapterType === 'bot_framework'
          ? `${baseUrl}/api/communicator/botframework/${translatedIntegration.id}/messages`
          : `${baseUrl}/api/communicator/webhook/${translatedIntegration.id}`;
        initialFormData['webhook_url'] = webhookUrl;
      }
      
      setFormData(initialFormData);
      setIntegrationTitle(translatedIntegration.title || translatedIntegration.label || '');
      const metadataGravado = (translatedIntegration.metadata ?? {}) as Record<string, unknown>;
      setModelEntries(modelsToEntries(metadataGravado.models));
      const { models: _models, ...demais } = metadataGravado;
      setMetadataValues(demais);
      setIsGlobal(translatedIntegration.scope === 'global');
    } else if (mode === 'create' && translatedTemplate) {
      // Modo de criação: SEMPRE inicializar campos vazios para segurança
      const initialFormData: Record<string, unknown> = {};
      
      // Inicializar campos de autenticação como vazios (SEGURANÇA)
      if (translatedTemplate.auth_fields?.fields) {
        translatedTemplate.auth_fields.fields.forEach((field: AuthField) => {
          initialFormData[field.key] = '';
        });
      }
      
      // Inicializar campos de configuração como vazios (SEGURANÇA)
      if (translatedTemplate.settings?.form_fields) {
        translatedTemplate.settings.form_fields.forEach((field: FormField) => {
          initialFormData[field.name] = '';
        });
      }
      
      setFormData(initialFormData);
    } else {
      // Limpar formData quando não há template/integração selecionada
      setFormData({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translatedTemplate, translatedIntegration, mode]);

  const handleFormFieldChange = (fieldName: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleCopyField = (fieldKey: string, value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleClose = () => {
    onOpenChange(false);
    // SEGURANÇA: Limpar completamente todos os dados do formulário
    setFormData({});
    setIsConnecting(false);
    setIsGlobal(false);
    setIntegrationTitle('');
    setModelEntries([]);
    setMetadataValues({});
    setActiveTab('settings');
  };

  const handleSubmit = async () => {
    if (mode === 'create' && !translatedTemplate) return;
    if (mode === 'edit' && !translatedIntegration) return;
    if (isConnecting) return;
    if (!effectiveOrgId) {
      toast({
        title: t("common.error"),
        description: t('integrations.scope.missingOrg', 'Não foi possível identificar a organização atual.'),
        variant: 'destructive'
      });
      return;
    }
    // Local scope só faz sentido com um workspace/produto selecionado.
    if (!isGlobal && !currentWorkspace) {
      toast({
        title: t("common.error"),
        description: t('integrations.scope.missingWorkspace', 'Selecione um workspace ou marque a integração como global.'),
        variant: 'destructive'
      });
      return;
    }

    // Campo de metadados declarado como obrigatório e vazio: conectar deixaria a integração
    // sem o que o provedor precisa — no Azure, sem nenhum deployment a chamar (AB#85605).
    const faltando = missingRequiredMetadata(metadataFields, metadataValues, modelEntries);
    if (faltando.length > 0) {
      setActiveTab('metadata');
      toast({
        title: t("common.error"),
        description: t('integrations.metadata.required'),
        variant: "destructive"
      });
      return;
    }

    // O catálogo é o que decide por qual endpoint cada modelo é chamado: modelo sem rota cai
    // em `chat` por omissão, e um de embedding chamado ali volta um erro que parece do
    // provedor (AB#85605).
    const problemasDeModelo = validateEntries(modelEntries);
    if (problemasDeModelo.length > 0) {
      // Leva à aba onde está o problema E diz qual linha: com trinta modelos na lista, o
      // aviso genérico manda procurar (AB#85605).
      setActiveTab('metadata');
      const detalhes = problemasDeModelo
        .slice(0, 3)
        .map((problema) =>
          problema.model
            ? t(`integrations.models.problem.${problema.reason}`, { model: problema.model })
            : t('integrations.models.problem.emptyName')
        )
        .join(' · ');
      toast({
        title: t("common.error"),
        description: detalhes,
        variant: "destructive"
      });
      return;
    }

    // Validar nome obrigatório
    if (!integrationTitle.trim()) {
      toast({
        title: t("common.error"),
        description: t('integrations.form.title.required'),
        variant: "destructive"
      });
      return;
    }

    setIsConnecting(true);

    let pendingPostInstall: IntegrationPostInstall | null = null;
    let pendingPostInstallLabel = '';

    try {
      // Validação básica de campos obrigatórios
      const missingFields: string[] = [];
      const authFields = mode === 'edit' 
        ? translatedIntegration!.template?.auth_fields || translatedIntegration!.auth_fields
        : translatedTemplate!.auth_fields;
      
      const authFieldsList = (authFields as { fields?: AuthField[] } | undefined)?.fields;
      if (authFieldsList) {
        // Validar apenas os campos definidos em auth_fields.fields
        authFieldsList.forEach((field: AuthField) => {
          if (field.required && (!formData[field.key] || String(formData[field.key]).trim() === '')) {
            missingFields.push(field.label || field.key);
          }
        });
      }
      
      if (missingFields.length > 0) {
        toast({
          title: t("common.error"),
          description: t('integrations.form.errors.requiredFields', { fields: missingFields.join(', ') }),
          variant: "destructive"
        });
        return;
      }
      
      if (mode === 'create') {
        // Separar dados de autenticação e configurações
        const credentials: Record<string, unknown> = {};
        const settings: Record<string, unknown> = {};
        
        // Coletar credenciais (auth_fields.fields)
        if (translatedTemplate!.auth_fields?.fields) {
          translatedTemplate!.auth_fields.fields.forEach((field: AuthField) => {
            if (formData[field.key] !== undefined) {
              credentials[field.key] = formData[field.key];
            }
          });
        }
        
        // Coletar APENAS OS VALORES dos campos de settings (não a estrutura do template)
        if (selectedTemplate?.settings?.form_fields) {
          selectedTemplate.settings.form_fields.forEach((field) => {
            if (field.readonly) return; // campos readonly são computados, não persistidos
            const formKey = field.key || field.name;    // chave usada no form input
            const storageKey = (field.name || field.key) ?? '';  // chave usada no backend (validation, MCP)
            const value = formData[formKey] || field.default || '';
            if (value !== undefined && value !== '') {
              settings[storageKey] = value;
            }
          });
        }
        
        // Criar nova integração
        const integrationData = {
          // `label` descreve o PROVEDOR (vem do template) e `title`, esta instalação — é o
          // que a lista mostra (`title || label`). O nome digitado ia para `title`, mas o
          // backend o descartava: a coluna existia e o model não a mapeava, então toda
          // instalação ficava com o nome do template e renomear não mudava nada (AB#85605).
          label: translatedTemplate!.label,
          title: integrationTitle.trim() || undefined,
          ...(metadataFields.length > 0
            ? {
                metadata: {
                  ...metadataValues,
                  ...(hasModelsField ? { models: entriesToModels(modelEntries) } : {}),
                },
              }
            : {}),
          description: (formData.description as string) || getIntegrationDescription(translatedTemplate!, i18n.language) || 'Ferramenta configurada via Prodgy',
          integration_id: translatedTemplate!.id,
          product_id: !isGlobal && currentWorkspace ? currentWorkspace.id : undefined,
          status: 'active' as IntegrationStatus,
          credentials,
          settings
        };
        
        const createResult = await IntegrationBaseService.create(integrationData, organizationId);
        if (createResult.post_install) {
          pendingPostInstall = createResult.post_install;
          pendingPostInstallLabel = translatedTemplate!.label || translatedTemplate!.name;
        }

      } else {
        // Editar integração existente - usar a mesma lógica do create
        const credentials: Record<string, unknown> = {};
        const settings: Record<string, unknown> = {};
        
        // Coletar credenciais (auth_fields.fields) - mesma lógica do create
        const editAuthFields = translatedIntegration!.auth_fields as { fields?: AuthField[] } | undefined;
        if (editAuthFields?.fields) {
          editAuthFields.fields.forEach((field: AuthField) => {
            if (formData[field.key] !== undefined) {
              credentials[field.key] = formData[field.key];
            }
          });
        }
        
        // Coletar APENAS OS VALORES dos campos de settings (usar template como referência)
        if (selectedIntegration?.template?.settings?.form_fields) {
          selectedIntegration.template.settings.form_fields.forEach((field: FormField) => {
            if (field.readonly) return; // campos readonly são computados, não persistidos
            const formKey = field.key || field.name;    // chave usada no form input
            const storageKey = (field.name || field.key) ?? '';  // chave usada no backend (validation, MCP)
            const value = formData[formKey];
            if (value !== undefined && value !== '') {
              settings[storageKey] = value;
            }
          });
        }
        
        const newScope = isGlobal ? 'global' : 'local';
        const updateData = {
          label: formData.label as string,
          title: integrationTitle.trim() || null,
          ...(metadataFields.length > 0
            ? {
                metadata: {
                  ...metadataValues,
                  ...(hasModelsField ? { models: entriesToModels(modelEntries) } : {}),
                },
              }
            : {}),
          description: ((formData.description as string) || getIntegrationDescription(translatedIntegration!, i18n.language)) ?? undefined,
          credentials, // Mesmo formato do create
          settings,    // Mesmo formato do create
          scope: newScope as 'local' | 'global',
          product_id: !isGlobal && currentWorkspace ? currentWorkspace.id : undefined,
        };
        
        const updateResult = await IntegrationBaseService.update(
          translatedIntegration!.id,
          updateData,
          organizationId,
        );
        if (updateResult.post_install) {
          pendingPostInstall = updateResult.post_install;
          pendingPostInstallLabel = translatedIntegration!.label || translatedIntegration!.name;
        }
      }
      
      // Invalidar cache para recarregar lista
      await queryClient.invalidateQueries({ queryKey: ["integrations-base", effectiveOrgId] });

      // Disparar evento para tour completar step de criar integração
      if (mode === 'create') {
        window.dispatchEvent(new Event('tour-integration-created'));
      }

      // Track success
      if (mode === 'create' && currentWorkspace) {
        analyticsService.trackEvent('integration_added', {
          integration_name: translatedTemplate!.name || translatedTemplate!.label,
          integration_type: translatedTemplate!.auth_type,
          product_id: currentWorkspace.id,
          has_custom_settings: selectedTemplate?.settings?.form_fields ? selectedTemplate.settings.form_fields.length > 0 : false
        });
      } else if (mode === 'edit') {
        analyticsService.trackEvent('integration_edited', {
          integration_name: translatedIntegration!.name || translatedIntegration!.label,
          integration_id: translatedIntegration!.id,
          fields_changed: Object.keys(formData)
        });
      }
      
      // Sucesso: fechar o modal do formulário. Quando o backend devolve
      // post_install (ex: comunicadores), o modal pós-instalação permanece
      // aberto até o usuário confirmar; só então notificamos o pai.
      onOpenChange(false);
      setFormData({});
      setIsConnecting(false);
      setIsGlobal(false);
      setIntegrationTitle('');
    setModelEntries([]);
    setMetadataValues({});
    setActiveTab('settings');
      if (pendingPostInstall) {
        setPostInstallInfo(pendingPostInstall);
        setPostInstallLabel(pendingPostInstallLabel);
      } else {
        onSuccess();
      }

    } catch (error) {
      const action = mode === 'edit' ? 'update' : 'create';
      console.error(`Failed to ${action} integration:`, error);
      const errorMessage = error instanceof Error ? error.message : t('integrations.form.errors.unknown');
      
      toast({
        title: t("common.error"),
        // A razão que o backend deu — "o provedor recusou as credenciais", por exemplo —
        // em vez do texto genérico. Era ele que transformava uma credencial recusada pelo
        // provedor num "confira os dados e tente novamente" que não dizia o quê (AB#85605).
        description: resolveApiErrorMessage(
          error,
          t as (key: string, options?: Record<string, unknown>) => string
        ),
        variant: "destructive"
      });

      // Track failure
      if (mode === 'create' && currentWorkspace) {
        analyticsService.trackEvent('integration_add_failed', {
          integration_name: translatedTemplate!.name || translatedTemplate!.label,
          integration_type: translatedTemplate!.auth_type,
          product_id: currentWorkspace.id,
          error_message: errorMessage
        });
      }
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            {(mode === 'edit' ? (translatedIntegration?.logo_url || translatedIntegration?.template?.logo_url) : translatedTemplate?.logo_url) && (
              <img
                src={mode === 'edit' ? (translatedIntegration?.logo_url || translatedIntegration?.template?.logo_url || '') : (translatedTemplate?.logo_url || '')}
                alt={mode === 'edit' ? (translatedIntegration?.label || '') : (translatedTemplate?.label || '')}
                className="h-8 w-8 object-contain"
              />
            )}
            <div>
              <DialogTitle className="text-left">
                {mode === 'edit' ? t('integrations.editDialog.editTitle', { name: translatedIntegration?.label ?? '' }) : translatedTemplate?.label}
              </DialogTitle>
              <DialogDescription className="text-left text-sm">
                {mode === 'edit' 
                  ? (getIntegrationDescription(translatedIntegration!, i18n.language) || t('integrations.editDialog.updateSettings'))
                  : (getIntegrationDescription(translatedTemplate!, i18n.language) || t('integrations.editDialog.configureIntegration'))}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        {/* Nome da integração (primeiro campo) */}
        <div className="space-y-2">
          <label className="text-sm font-medium">
            {t('integrations.form.title.label')}
            <span className="text-destructive ml-1">*</span>
          </label>
          <Input
            placeholder={t('integrations.form.title.placeholder')}
            value={integrationTitle}
            onChange={(e) => setIntegrationTitle(e.target.value)}
          />
        </div>

        {/* Catálogo de modelos em aba própria: ele tem dezenas de linhas e, no corpo do
            formulário, empurrava a credencial e o botão de salvar para fora da tela
            (AB#85605). Sem modelos a cadastrar, o diálogo segue sem abas. */}
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as 'settings' | 'metadata')}
          className="flex min-h-0 flex-1 flex-col"
        >
          {metadataFields.length > 0 && (
            <TabsList className="self-start">
              <TabsTrigger value="settings">{t('integrations.sections.settings')}</TabsTrigger>
              <TabsTrigger value="metadata">{t('integrations.metadata.tab')}</TabsTrigger>
            </TabsList>
          )}

          <TabsContent
            value="settings"
            className="scroll-slim mt-3 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1"
          >
        {/* Formulário dinâmico baseado na nova estrutura */}
        {/* Campo honeypot invisível para confundir autocomplete */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
          tabIndex={-1}
          aria-hidden="true"
        />
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
          tabIndex={-1}
          aria-hidden="true"
        />
        
        <div className="space-y-4">
          {/* Campos de Configurações - apresentados primeiro */}
          {((mode === 'edit' ? translatedIntegration?.template?.settings : translatedTemplate?.settings)?.form_fields || []).length > 0 && (
            <div className="space-y-4">
              {/* Com a aba de metadados, o título repetiria o nome da própria aba; sem ela,
                  é o que separa as configurações da autenticação (AB#85605). */}
              {metadataFields.length === 0 && (
                <h3 className="text-sm font-semibold text-muted-foreground border-b pb-2">
                  {t('integrations.sections.settings')}
                </h3>
              )}
              {((mode === 'edit' ? translatedIntegration?.template?.settings : translatedTemplate?.settings)?.form_fields || []).map((field: FormField) => {
                const fieldKey = field.key || field.name;
                return (
                <div key={fieldKey} className="space-y-2">
                  <label className="text-sm font-medium">
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </label>
                  {field.description && (
                    <p
                      className="text-xs text-muted-foreground [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(field.description) }}
                    />
                  )}

                  {field.readonly ? (
                    <div className="flex items-center gap-2">
                      {/* Cai no default do template: campo travado não é editável,
                          então até o primeiro save não há valor na instância — e sem
                          isto a pessoa vê um campo obrigatório em branco. O submit já
                          grava `field.default` com a mesma expressão. */}
                      <Input
                        type="text"
                        value={String(formData[fieldKey] || field.default || '')}
                        readOnly
                        className="font-mono text-xs bg-muted text-muted-foreground cursor-default select-all"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0 h-9 w-9"
                        onClick={() => handleCopyField(fieldKey, String(formData[fieldKey] || ''))}
                        title={t('common.copy', 'Copiar')}
                      >
                        {copiedField === fieldKey ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  ) : field.type === 'password' ? (
                    <PasswordField
                      field={{ ...field, name: fieldKey }}
                      value={String(formData[fieldKey] || '')}
                      onChange={(value) => handleFormFieldChange(fieldKey, value)}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      value={String(formData[fieldKey] || '')}
                      onChange={(e) => handleFormFieldChange(fieldKey, e.target.value)}
                      className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                      required={field.required}
                    >
                      <option value="">{t('integrations.placeholders.select')}</option>
                      {field.options?.map((option: string) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : field.type === 'checkbox' ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={Boolean(formData[fieldKey])}
                        onChange={(e) => handleFormFieldChange(fieldKey, e.target.checked)}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                    </div>
                  ) : (
                    <Input
                      type={field.type || 'text'}
                      placeholder={field.placeholder || t('integrations.placeholders.enterField', { field: field.label.toLowerCase() })}
                      value={String(formData[fieldKey] || '')}
                      onChange={(e) => handleFormFieldChange(fieldKey, e.target.value)}
                      required={field.required}
                      autoComplete="new-password"
                      name={`field_${fieldKey}_${Date.now()}`}
                      readOnly
                      onFocus={(e) => e.target.removeAttribute('readonly')}
                    />
                  )}
                </div>
              )})}
            </div>
          )}
          
          {/* Campos de Autenticação - apresentados por último */}
          {((mode === 'edit' ? translatedIntegration?.template?.auth_fields : translatedTemplate?.auth_fields)?.fields || []).length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground border-b pb-2">{t('integrations.sections.authentication')}</h3>
              {((mode === 'edit' ? translatedIntegration?.template?.auth_fields : translatedTemplate?.auth_fields)?.fields || []).map((field: AuthField) => (
                <div key={field.key} className="space-y-2">
                  <label className="text-sm font-medium">
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </label>
                  {field.description && (
                    <p
                      className="text-xs text-muted-foreground [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(field.description) }}
                    />
                  )}

                  {field.type === 'password' || field.secret ? (
                    <PasswordField
                      field={{ name: field.key, ...field }}
                      value={String(formData[field.key] || '')}
                      onChange={(value) => handleFormFieldChange(field.key, value)}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      value={String(formData[field.key] || '')}
                      onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
                      className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                      required={field.required}
                      autoComplete="new-password"
                      name={`select_${field.key}_${Date.now()}`}
                    >
                      <option value="">{t('integrations.placeholders.select')}</option>
                      {field.options?.map((option: string) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      type={field.type || 'text'}
                      placeholder={t('integrations.placeholders.enterField', { field: field.label.toLowerCase() })}
                      value={String(formData[field.key] || '')}
                      onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
                      required={field.required}
                      autoComplete="new-password"
                      name={`auth_${field.key}_${Date.now()}`}
                      readOnly
                      onFocus={(e) => e.target.removeAttribute('readonly')}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Escopo da integração — oculto na Visão da organização (sempre global) */}
          {!isOrgView && (
            <div className="space-y-3 pt-2 border-t">
              <h3 className="text-sm font-semibold text-muted-foreground">{t('integrations.scope.permissionsLabel')}</h3>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="integration-scope"
                    checked={!isGlobal}
                    onChange={() => setIsGlobal(false)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                  />
                  <span className="text-sm font-medium">{t('integrations.scope.productLabel')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="integration-scope"
                    checked={isGlobal}
                    onChange={() => setIsGlobal(true)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                  />
                  <span className="text-sm font-medium">{t('integrations.scope.organizationLabel')}</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                {isGlobal
                  ? t('integrations.scope.organizationHint')
                  : t('integrations.scope.productHint')}
              </p>
            </div>
          )}

          {/* Campos padrão apenas no modo create quando não há auth_fields */}
          {mode === 'create' && (!translatedTemplate?.auth_fields || Object.keys(translatedTemplate.auth_fields).length === 0) && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('integrations.defaultFields.integrationName')} <span className="text-destructive ml-1">*</span>
                </label>
                <Input
                  placeholder={t('integrations.defaultFields.integrationNamePlaceholder')}
                  value={String(formData.name || '')}
                  onChange={(e) => handleFormFieldChange('name', e.target.value)}
                  required
                  autoComplete="new-password"
                  name={`name_${Date.now()}`}
                  readOnly
                  onFocus={(e) => e.target.removeAttribute('readonly')}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('integrations.defaultFields.apiKeyToken')} <span className="text-destructive ml-1">*</span></label>
                <PasswordField 
                  field={{ name: 'api_key', label: 'API Key', type: 'password', required: true }}
                  value={String(formData.api_key || '')}
                  onChange={(value) => handleFormFieldChange('api_key', value)}
                />
              </div>
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="metadata" className="scroll-slim mt-3 min-h-0 flex-1 overflow-y-auto pr-1">
            <IntegrationMetadataFields
              fields={metadataFields}
              values={metadataValues}
              onValueChange={(key, value) =>
                setMetadataValues((atual) => ({ ...atual, [key]: value }))
              }
              modelEntries={modelEntries}
              onModelEntriesChange={setModelEntries}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex gap-2 pt-4">
          <Button 
            variant="outline" 
            onClick={handleClose}
            className="flex-1"
            disabled={isConnecting}
          >
            {t('integrations.actions.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit}
            className="flex-1"
            disabled={(mode === 'create' && !translatedTemplate) || (mode === 'edit' && !translatedIntegration) || isConnecting}
          >
            {isConnecting 
              ? (mode === 'edit' ? t('integrations.actions.updating') : t('integrations.actions.connecting')) 
              : (mode === 'edit' ? t('integrations.actions.update') : t('integrations.actions.connect'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {postInstallInfo && (
      <IntegrationPostInstallDialog
        isOpen={true}
        onClose={() => {
          setPostInstallInfo(null);
          setPostInstallLabel('');
          onSuccess();
        }}
        integrationLabel={postInstallLabel}
        postInstall={postInstallInfo}
      />
    )}
    </>
  );
};
